import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Heart, Shield, Rocket, Code2, Briefcase, Award, Coffee, Twitter, Linkedin } from 'lucide-react';

const TEAM = [
  { name: 'Marcus Vale',     role: 'Founder & CEO',           bio: 'Ex-Jump Trading quant. Solana since genesis block.',             img: 'https://images.unsplash.com/photo-1581913229425-9c6b993fc107?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Yelena Petrov',   role: 'CTO',                     bio: 'Former Jane Street engineer. Distributed systems wizard.',        img: 'https://images.unsplash.com/photo-1573497620166-aef748c8c792?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Arjun Reddy',     role: 'Lead Blockchain Engineer', bio: 'Wrote half of pump.fun integrations nobody else can.',          img: 'https://images.unsplash.com/photo-1732209556859-72cfe81ae9ab?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Sofia Blanc',     role: 'Head of Security',        bio: 'Led audits at Trail of Bits. Broke 3 L1s before breakfast.',    img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Daniel Okonkwo',  role: 'ML Research',             bio: 'PhD Stanford. Built the rugpull shield neural net.',            img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Mei Tanaka',      role: 'Head of Product',         bio: 'Previously led trading UI at FTX (RIP). Sharp eye for UX.',     img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Tobias Graf',     role: 'Infra Lead',              bio: 'Runs our 38-node Solana RPC cluster. Uptime: 99.99%.',          img: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=faces' },
  { name: 'Hannah Cohen',    role: 'Community Lead',          bio: 'Built the 42k Telegram community from 0. Reads every ticket.',   img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=faces' },
];

const VALUES = [
  { Icon: Shield,    t: 'Security first',    d: 'Non-custodial by design. Your keys, your tokens, always. Two public audits and a $100K bug bounty.' },
  { Icon: Rocket,    t: 'Ship fast',         d: 'Weekly releases. 30+ meaningful features shipped in 2025 alone.' },
  { Icon: Heart,     t: 'Community owned',   d: '42,000+ traders shape the roadmap. Feature voting baked into the terminal.' },
  { Icon: Code2,     t: 'Open by default',   d: 'Open API, open SDK, open specs. Fork it, build on it, make it yours.' },
];

const STATS = [
  { v: '2022',   k: 'Founded' },
  { v: '42K+',   k: 'Users' },
  { v: '136K+',  k: 'Snipes' },
  { v: '$8.4M',  k: 'Daily volume' },
  { v: '8',      k: 'Countries' },
  { v: '24/7',   k: 'Support' },
];

export default function About() {
  return (
    <div className="pt-12 sm:pt-16 pb-16 sm:pb-20" data-testid="about-page">
      <section className="container-x text-center py-10">
        <span className="chip"><Users className="w-3 h-3" /> About us</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          We build the fastest <span className="gradient-text">sniper</span> on Solana
        </h1>
        <p className="text-muted-foreground mt-5 max-w-2xl mx-auto leading-relaxed">
          A distributed team of 23 engineers, researchers and traders across 8 countries.
          We got tired of losing to bots. So we built a better one and shared it with the world.
        </p>
      </section>

      <section className="container-x grid grid-cols-3 md:grid-cols-6 gap-3 mt-6">
        {STATS.map((s) => (
          <div key={s.k} className="glass-card p-4 text-center">
            <div className="font-display font-black text-2xl tracking-tight">{s.v}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{s.k}</div>
          </div>
        ))}
      </section>

      <section className="container-x mt-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="chip"><Target className="w-3 h-3" /> Mission</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-3">
              Level the playing field.
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Memecoin launches used to be won by the five whales with custom infra. We put that same infra in a browser tab,
              priced it at a fraction of a centralized trading desk, and audited it publicly.
            </p>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Every new feature asks the same question: does this help an everyday trader beat the pro?
              If the answer is yes, we ship it this week.
            </p>
          </div>
          <div className="glass-card p-6">
            <h3 className="font-display font-bold text-lg mb-5 flex items-center gap-2"><Briefcase className="w-4 h-4 text-[hsl(var(--primary))]" /> By the numbers</h3>
            <div className="space-y-4">
              {[
                { k: 'Trades executed', v: '136,482' },
                { k: 'Volume (lifetime)', v: '$412M+' },
                { k: 'Successful audits', v: '2 (CertiK + OZ)' },
                { k: 'Bug bounty pool', v: '$100,000' },
                { k: 'Median latency', v: '8ms' },
                { k: 'Uptime (2025)', v: '99.97%' },
              ].map((r) => (
                <div key={r.k} className="flex items-center justify-between pb-3 border-b border-border/40 last:border-0 last:pb-0 font-mono text-sm">
                  <span className="text-muted-foreground">{r.k}</span>
                  <span className="font-bold text-[hsl(var(--primary))]">{r.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-x mt-20">
        <div className="text-center mb-10">
          <span className="chip"><Award className="w-3 h-3" /> Values</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
            What we <span className="gradient-text">believe</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {VALUES.map((v, i) => (
            <motion.div key={v.t}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass-card glass-card-hover p-6">
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))]">
                <v.Icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-base mt-4">{v.t}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="container-x mt-20">
        <div className="text-center mb-10">
          <span className="chip"><Coffee className="w-3 h-3" /> The team</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
            Traders, engineers, <span className="gradient-text">degens</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((m, i) => (
            <motion.div key={m.name}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="glass-card glass-card-hover p-5 group" data-testid={`team-member-${i}`}>
              <div className="relative rounded-xl overflow-hidden aspect-square mb-4">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                  {[Twitter, Linkedin].map((Ic, j) => (
                    <a key={j} href="#" className="w-7 h-7 rounded-lg bg-black/50 backdrop-blur flex items-center justify-center text-white/80 hover:text-[hsl(var(--primary))] transition-colors">
                      <Ic className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              </div>
              <div className="font-display font-bold text-base">{m.name}</div>
              <div className="text-[hsl(var(--primary))] text-xs font-mono uppercase tracking-wider mt-0.5">{m.role}</div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{m.bio}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="container-x mt-16">
        <div className="glass-card p-6 sm:p-8 text-center">
          <h3 className="font-display font-bold text-2xl">Join the pack.</h3>
          <p className="text-muted-foreground mt-2">We are always hiring obsessive Solana builders.</p>
          <a href="#/contact" className="btn-primary mt-5">
            <Briefcase className="w-4 h-4" /> See open roles
          </a>
        </div>
      </section>
    </div>
  );
}
